import { constituencyFor } from './lobbyScheduleUtils'
import type {
  AttendanceStatus, ScheduleAssignmentDto, ScheduleAttendeeDto, ScheduleLegislatorRowDto,
  ScheduleMeetingDto, LobbyMeetingStatus, MeetingWith, MeetingRole, MeetingStaffDto, AskResponse,
} from '../../../types/lobbyDay'
import { uuid } from '../../../lib/uuid'

/** Build a fresh (unsaved) assignment, computing constituency against the meeting's legislator.
 *  Mirrors LobbyScheduler.handleDragEnd's local `makeAssignment` (new: prefix + constituencyFor). */
export function makeAssignment(
  attendee: ScheduleAttendeeDto, legislator: ScheduleLegislatorRowDto,
): ScheduleAssignmentDto {
  return {
    assignmentId: `new:${uuid()}`,
    attendeeId: attendee.attendeeId,
    fullName: attendee.fullName,
    constituency: constituencyFor(attendee, legislator),
    attendance: 'Unmarked',
    role: 'Member',
  }
}

/** Add an attendee to a meeting; no-op if that attendee is already assigned there. */
export function assignAttendee(
  meetings: ScheduleMeetingDto[], meetingId: string,
  attendee: ScheduleAttendeeDto, legislator: ScheduleLegislatorRowDto,
): ScheduleMeetingDto[] {
  return meetings.map((m) => {
    if (m.id !== meetingId) return m
    if (m.assignments.some((x) => x.attendeeId === attendee.attendeeId)) return m
    return { ...m, assignments: [...m.assignments, makeAssignment(attendee, legislator)] }
  })
}

/** Remove a single assignment from its meeting. */
export function unassignAttendee(
  meetings: ScheduleMeetingDto[], meetingId: string, assignmentId: string,
): ScheduleMeetingDto[] {
  return meetings.map((m) =>
    m.id === meetingId
      ? { ...m, assignments: m.assignments.filter((a) => a.assignmentId !== assignmentId) }
      : m)
}

/** Move an assignment to a different meeting, recomputing constituency for the target legislator.
 *  Same-meeting is a no-op (returns the same array). If the target already has the attendee,
 *  the source assignment is removed and no duplicate is added. Mirrors the assignment→meeting
 *  branch of LobbyScheduler.handleDragEnd. */
export function moveAssignment(
  meetings: ScheduleMeetingDto[], assignmentId: string, fromMeetingId: string, toMeetingId: string,
  attendee: ScheduleAttendeeDto, targetLegislator: ScheduleLegislatorRowDto,
): ScheduleMeetingDto[] {
  if (fromMeetingId === toMeetingId) return meetings
  const target = meetings.find((m) => m.id === toMeetingId)
  if (!target) return meetings
  const alreadyThere = target.assignments.some((x) => x.attendeeId === attendee.attendeeId)
  const fresh = alreadyThere ? null : makeAssignment(attendee, targetLegislator)
  return meetings.map((m) => {
    if (m.id === fromMeetingId) return { ...m, assignments: m.assignments.filter((a) => a.assignmentId !== assignmentId) }
    if (m.id === toMeetingId && fresh) return { ...m, assignments: [...m.assignments, fresh] }
    return m
  })
}

export function addMeeting(meetings: ScheduleMeetingDto[], meeting: ScheduleMeetingDto): ScheduleMeetingDto[] {
  return [...meetings, meeting]
}

export function removeMeeting(meetings: ScheduleMeetingDto[], meetingId: string): ScheduleMeetingDto[] {
  return meetings.filter((m) => m.id !== meetingId)
}

/** Change a meeting's start/end time, preserving date and every other field.
 *  No-op if the id is not present. */
export function rescheduleMeeting(
  meetings: ScheduleMeetingDto[], meetingId: string, startTime: string, endTime: string,
): ScheduleMeetingDto[] {
  return meetings.map((m) => (m.id === meetingId ? { ...m, startTime, endTime } : m))
}

/** Set (or clear) a meeting's ask. askTitle is stored alongside so cards/chips reflect it
 *  without a refetch; persisted via buildSaveRequest (askId). */
export function setMeetingAsk(
  meetings: ScheduleMeetingDto[], meetingId: string, askId: string | null, askTitle: string | null,
): ScheduleMeetingDto[] {
  return meetings.map((m) => (m.id === meetingId ? { ...m, askId, askTitle } : m))
}

/** Set (or clear) a meeting's room free-text. */
export function setMeetingRoom(
  meetings: ScheduleMeetingDto[], meetingId: string, room: string | null,
): ScheduleMeetingDto[] {
  return meetings.map((m) => (m.id === meetingId ? { ...m, room } : m))
}

/** Set a meeting's lifecycle status. Persists through buildSaveRequest, which already
 *  sends `status` on every upsert — no save-protocol change is needed. */
export function setMeetingStatus(
  meetings: ScheduleMeetingDto[], meetingId: string, status: LobbyMeetingStatus,
): ScheduleMeetingDto[] {
  return meetings.map((m) => (m.id === meetingId ? { ...m, status } : m))
}

/** Set a single assignment's day-of attendance. Persisted out-of-band by the immediate
 *  set-attendance endpoint — NOT by buildSaveRequest — and excluded from the dirty-compare. */
export function setAssignmentAttendance(
  meetings: ScheduleMeetingDto[], meetingId: string, assignmentId: string, attendance: AttendanceStatus,
): ScheduleMeetingDto[] {
  return meetings.map((m) =>
    m.id === meetingId
      ? { ...m, assignments: m.assignments.map((a) => (a.assignmentId === assignmentId ? { ...a, attendance } : a)) }
      : m)
}

/** Set who a meeting is with (legislator / staff / both). A staged edit, persisted through
 *  buildSaveRequest's `withWhom`. */
export function setMeetingWithWhom(
  meetings: ScheduleMeetingDto[], meetingId: string, withWhom: MeetingWith,
): ScheduleMeetingDto[] {
  return meetings.map((m) => (m.id === meetingId ? { ...m, withWhom } : m))
}

/** Set an assignment's delegation role. Making someone `Lead` demotes every other lead on that
 *  meeting (at most one lead, mirroring the server). Out-of-band, like attendance. */
export function setAssignmentRole(
  meetings: ScheduleMeetingDto[], meetingId: string, assignmentId: string, role: MeetingRole,
): ScheduleMeetingDto[] {
  return meetings.map((m) => {
    if (m.id !== meetingId) return m
    return {
      ...m,
      assignments: m.assignments.map((a) => {
        if (a.assignmentId === assignmentId) return { ...a, role }
        if (role === 'Lead' && a.role === 'Lead') return { ...a, role: 'Member' as const }
        return a
      }),
    }
  })
}

/** Replace a meeting's staff-tag list with the server's (the tag/untag endpoints return it). */
export function setMeetingStaff(
  meetings: ScheduleMeetingDto[], meetingId: string, staff: MeetingStaffDto[],
): ScheduleMeetingDto[] {
  return meetings.map((m) => (m.id === meetingId ? { ...m, staff } : m))
}

/** Set a meeting's recorded ask outcome (the outcome endpoint overwrites both fields). */
export function setMeetingOutcome(
  meetings: ScheduleMeetingDto[], meetingId: string, askResponse: AskResponse, responseNotes: string | null,
): ScheduleMeetingDto[] {
  return meetings.map((m) => (m.id === meetingId ? { ...m, askResponse, responseNotes } : m))
}
